import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Base query with authentication
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:4000/api',
  credentials: 'include', // This is important for sending/receiving cookies
  prepareHeaders: (headers, { getState, endpoint, extra, type, forced, body }) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    // Set authorization header with access token
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    
    // Only set Content-Type if it's not FormData
    // The browser will automatically set the correct Content-Type with boundary for FormData
    
    return headers;
  },
});

// --- Refresh-token deduplication ----------------------------------------------
// During an exam many requests fire in parallel. When the access token expires
// every request gets a 401, so without a mutex we'd hit `/auth/refresh-token`
// many times concurrently. That race used to cause spurious failures and
// kicked the user out mid-test. Here we ensure only ONE refresh is in flight
// at a time; concurrent callers await the same promise.
let refreshPromise = null;

const performRefresh = async (api, extraOptions) => {
  if (!refreshPromise) {
    // The refresh token lives in an HttpOnly cookie set by the BE on login.
    // `credentials: 'include'` ensures the browser attaches it automatically.
    // We deliberately do NOT read it from JS storage — keeping it out of
    // localStorage is the whole point of using an HttpOnly cookie.
    refreshPromise = baseQuery(
      {
        url: '/auth/refresh-token',
        method: 'POST',
        credentials: 'include',
      },
      api,
      extraOptions,
    ).finally(() => {
      setTimeout(() => { refreshPromise = null; }, 0);
    });
  }
  return refreshPromise;
};

// Notify the app that the session ended (handled by AuthContext / route guards).
// We deliberately do NOT call `window.location.href = '/login'` here – a hard
// reload would destroy in-progress mock-test state. Route guards / the auth
// slice are responsible for redirecting to login when `isAuthenticated` flips.
//
// Additionally, when the user is in the middle of an exam attempt we DO NOT
// clear credentials at all: doing so flips `isAuthenticated`, which causes
// `ProtectedRoute` to redirect to /login and wipes every answer they have
// in component state. Instead we just emit the event so a re-auth modal /
// banner can prompt the user to log in again, and the original 401 is
// surfaced to the caller so the submit button can be retried.
const isOnActiveExamPage = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location?.pathname || '';
  // Matches /exam-session/<id> and /exam/<id>/session etc. Adjust if routes
  // change. Cheap and dependency-free so it works from inside baseQuery.
  return /\/exam[-/]?session/i.test(path);
};

const dispatchSessionExpired = () => {
  try {
    if (isOnActiveExamPage()) {
      // Keep the access token / user in localStorage so the answers and
      // attempt id stay alive in Redux. The app surfaces a re-login prompt
      // via the `auth:session-expired` event without forcing a redirect.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:session-expired', {
          detail: { keepSession: true },
        }));
      }
      return;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }
  } catch (e) {
    // ignore
  }
};

// Determine whether a failed refresh response represents a *real* auth
// failure (refresh token invalid/expired) versus a transient network/server
// problem. Only the former should end the user's session.
//
// Real cases that DO end the session: 401 / 403 from the refresh endpoint
// itself with a JSON body. The BE already rotates / clears the cookie.
//
// Transient cases that should NOT end the session:
//   - `FETCH_ERROR` — browser couldn't reach the server (e.g. the OS just
//     resumed from sleep and the network stack isn't ready yet, captive
//     portal, VPN reconnect, etc.).
//   - `TIMEOUT_ERROR`
//   - 5xx server errors
//   - "PARSING_ERROR" without an HTTP status
// In these cases we surface the original 401 to the caller as a normal
// error; the next user action (or RTK Query refetch) will try again.
const isAuthFailure = (refreshResult) => {
  if (!refreshResult) return false;
  if (refreshResult.data?.token) return false; // success
  const status = refreshResult.error?.status;
  return status === 401 || status === 403;
};

// Base query with re-auth
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Only attempt reauth if user had a token (was previously authenticated)
  const hadToken = !!localStorage.getItem('token');

  // If the error is 401/403, try to refresh the token (once, deduplicated).
  if ((result?.error?.status === 401 || result?.error?.status === 403) && hadToken) {
    // Don't try to refresh if the failing call IS the refresh endpoint itself.
    const url = typeof args === 'string' ? args : args?.url;
    if (url && url.includes('/auth/refresh-token')) {
      dispatchSessionExpired();
      return result;
    }

    // If the browser knows we're offline (e.g. user closed laptop, OS just
    // unlocked and Wi-Fi hasn't reassociated yet), don't even try to
    // refresh — return the original error so RTK Query will retry on the
    // next request. Killing the session here is exactly what caused
    // students to get logged out mid-exam after locking their machine.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return result;
    }

    try {
      const refreshResult = await performRefresh(api, extraOptions);

      if (refreshResult?.data?.token) {
        const { token } = refreshResult.data;
        localStorage.setItem('token', token);
        // Rotated refresh token (if any) is delivered via Set-Cookie by the BE
        // and stored as an HttpOnly cookie. We never touch it from JS.

        // Retry the original request with the new token
        const retryArgs = typeof args === 'string'
          ? {
              url: args,
              credentials: 'include',
              headers: { authorization: `Bearer ${token}` },
            }
          : {
              ...args,
              credentials: 'include',
              headers: {
                ...args.headers,
                authorization: `Bearer ${token}`,
              },
            };

        result = await baseQuery(retryArgs, api, extraOptions);
      } else if (isAuthFailure(refreshResult)) {
        // Refresh endpoint explicitly rejected the refresh token (401/403).
        // The session is genuinely over — clear creds and let route guards
        // redirect to /login.
        dispatchSessionExpired();
      } else {
        // Transient failure (FETCH_ERROR / 5xx / timeout). Do NOT log the
        // user out — keep credentials intact and surface the original 401
        // to the caller so the next attempt can refresh successfully once
        // the network is back. This is the critical fix for "locked OS =>
        // logged out mid mock-test".
        console.warn(
          '[auth] Refresh token call failed transiently, keeping session:',
          refreshResult?.error,
        );
      }
    } catch (error) {
      // Same reasoning as above: thrown errors here are network-level
      // (RTK Query normally returns errors as data, not throws), so treat
      // as transient.
      console.error('Failed to refresh token (transient):', error);
    }
  }

  return result;
};

// Create the base API slice
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User', 
    'Assignment', 
    'Exam', 
    'AssignmentSubmission', 
    'ExamAttempt',
    'Analytics',
    'Dashboard',
    'Exercise',
    'ExerciseDay',
    'ExercisePart',
    'ExerciseQuestion',
    'ExerciseAssignment',
    'Class',
    'DialogueQuestion',
    'StudentExerciseStats',
    'AdminExerciseReport',
    'AdminMockTestReport',
    'CourseAd',
    'Feedback',
    'Ranking',
    'TrialLab',
    'TrialLabTest',
    'TrialLabSession',
    'TrialLabHistory',
    'VocabularyMemory',
    'VocabularyMemoryStats',
    'PublicMockTest',
    'PublicMockTestHistory',
    'PublicMockTestStats',
    'Book'
  ],
  endpoints: () => ({}),
});

export default baseApi;
